// Centralized chart palette. Tailwind chart wrappers read the user's primary
// color from the app store and derive the multi-series palette by HSL rotation.

import { useMemo } from 'react';
import { useAppStore } from '../../shared/stores/useAppStore';

export const CHART_PRIMARY = '#135bec';

// Hue rotations (degrees) applied to the primary to generate the multi-series
// palette. Order picked so neighbouring series stay visually distinct.
const HUE_ROTATIONS = [0, 150, 60, -90, 210, 30, -150, 90, 270, 120];

export const CHART_PALETTE: readonly string[] = generatePaletteFromPrimary(CHART_PRIMARY);

interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

export function isValidHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);
}

export function hexToHsl(hex: string): HSL {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case r: h = ((g - b) / delta + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / delta + 2); break;
      case b: h = ((r - g) / delta + 4); break;
    }
    h *= 60;
  }

  return { h, s: s * 100, l: l * 100 };
}

export function hslToHex({ h, s, l }: HSL): string {
  const hh = ((h % 360) + 360) % 360 / 360;
  const ss = Math.max(0, Math.min(100, s)) / 100;
  const ll = Math.max(0, Math.min(100, l)) / 100;

  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;

  const channel = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  const r = Math.round(channel(hh + 1 / 3) * 255);
  const g = Math.round(channel(hh) * 255);
  const b = Math.round(channel(hh - 1 / 3) * 255);

  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function generatePaletteFromPrimary(
  primaryHex: string,
  count: number = HUE_ROTATIONS.length,
): string[] {
  const safe = isValidHexColor(primaryHex) ? primaryHex : CHART_PRIMARY;
  const { h, s, l } = hexToHsl(safe);
  return Array.from({ length: count }, (_, i) => {
    const offset = HUE_ROTATIONS[i % HUE_ROTATIONS.length];
    return hslToHex({ h: h + offset, s, l });
  });
}

// Resolve the effective primary color: user preference if valid, else fallback.
export function resolveChartPrimaryColor(value: string | null | undefined): string {
  return isValidHexColor(value) ? value : CHART_PRIMARY;
}

// React hook: returns the user's primary color (or '#135bec' fallback).
export function useChartPrimaryColor(): string {
  const stored = useAppStore((state) => state.userSettings.chartPrimaryColor);
  return resolveChartPrimaryColor(stored);
}

// React hook: returns the user-derived palette, memoized on the primary color.
export function useChartPalette(count: number = HUE_ROTATIONS.length): string[] {
  const primary = useChartPrimaryColor();
  return useMemo(() => generatePaletteFromPrimary(primary, count), [primary, count]);
}

export function getChartColor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length];
}
